import { ExternalLinkIcon, LockClosedIcon, MagnifyingGlassIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useAuth } from '@/auth/auth-provider';
import { Button } from '@/components/ui/button';
import { findNavItem, type NavItem } from '../nav';

function StatusPage({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-20 text-center">
      <div className="grid size-12 place-items-center rounded-full bg-muted text-muted-foreground [&_svg]:size-5">
        {icon}
      </div>
      <h1 className="text-xl font-semibold">{title}</h1>
      {children}
    </div>
  );
}

export function NotFoundPage() {
  return (
    <StatusPage icon={<MagnifyingGlassIcon />} title="Page not found">
      <p className="text-sm text-muted-foreground">The address may be mistyped, or the page moved.</p>
      <Button asChild variant="outline">
        <Link to="/">Go home</Link>
      </Button>
    </StatusPage>
  );
}

export function NoAccessPage() {
  return (
    <StatusPage icon={<LockClosedIcon />} title="You don't have access to this page">
      <p className="text-sm text-muted-foreground">Ask an administrator for the permission if you need it.</p>
      <Button asChild variant="outline">
        <Link to="/">Go home</Link>
      </Button>
    </StatusPage>
  );
}

export function ComingSoonPage({ item }: { item: NavItem }) {
  return (
    <StatusPage icon={<UpdateIcon />} title={item.label}>
      <p className="text-sm text-muted-foreground">This area is being rebuilt in the new backoffice.</p>
      {item.legacyPath && (
        <Button asChild variant="outline">
          <a href={item.legacyPath}>
            Open it in the current backoffice <ExternalLinkIcon />
          </a>
        </Button>
      )}
    </StatusPage>
  );
}

/** The app's splat route: placeholder for pages not rebuilt yet, no-access, or 404. */
export function PlaceholderOrNotFound() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { can, superAdmin } = useAuth();
  const item = findNavItem(pathname);
  if (!item || item.ready) return <NotFoundPage />;
  if (!can(...item.permissions) || (item.superAdminOnly && !superAdmin)) return <NoAccessPage />;
  return <ComingSoonPage item={item} />;
}
